import { memo, useState, useEffect } from 'react';
import { Link, useHistory, useParams } from 'react-router-dom';
import {
  Popconfirm,
  Typography,
  Form,
  Input,
  Radio,
  notification,
  Card,
  Space,
  Alert,
  Skeleton,
  Descriptions,
  Row,
  Col,
  Checkbox,
  Modal,
  Select,
  InputNumber,
  Tooltip,
} from 'antd';

import BreadcrumbContainer from '../../../containers/Breadcrumb';
import CustomButton from '../../../components/CustomButton';
import INTERNAL_LINKS from '../../../enums/InternalLinks';
import MESSAGES from '../../../enums/Messages';
import {
  post,
  patch,
  remove,
  filter as filterAjaxTable,
  swap,
  info,
  preview,
  patchModule,
  get,
  renumerate,
  move,
  duplicate,
} from '../../../api/module/ctm_products';

import '../Form/style.scss';
import AjaxTable from '../../../components/AjaxTable';
import { catchException } from '../../../utils/helpers/helpers';
import EllipsisModal from '../../../components/EllipsisModal';
import ModificationModuleSaveButton from '../../../components/ModificationComponents';
import { CustomTooltip } from '../../../components/CustomTooltip';
import PaPreviewModal from './PAPreview';
import { getSupportTypes } from '../../../api/module/term_products';
import { CustomSelector, IconButton } from '../../../components';
import ProductFields from '../../Products/fields';
import IconColors from '../../../enums/IconColors';
import {
  CopyFilled,
  DeleteFilled,
  EditFilled,
  FullscreenOutlined,
  LoadingOutlined,
  SwapLeftOutlined,
  SwapOutlined,
  SwapRightOutlined,
} from '@ant-design/icons';

const { Title, Paragraph, Text } = Typography;
const { TextArea } = Input;

const DuplicateModal = ({ record, onClose }) => {
  const [dForm] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const onFinish = async (values) => {
    setLoading(true);
    try {
      await duplicate(record.id, values);
      setLoading(false);
      onClose(true);
    } catch (error) {
      setLoading(false);
      catchException(error);
    }
  };

  return (
    <Modal
      title="Duplicar producto"
      closable={!loading}
      footer={
        <CustomButton
          type="default"
          text="Duplicar"
          htmlType="button"
          loading={loading}
          onClick={() => dForm.submit()}
        />
      }
      onCancel={() => onClose()}
      open={record?.id}
      destroyOnClose
    >
      <Form
        form={dForm}
        onFinish={onFinish}
        layout="vertical"
        initialValues={{ n: 0 }}
      >
        <Form.Item
          name="n"
          label="¿Cuántas veces quiere duplicar este producto?"
          rules={[
            { required: true, message: 'Indique un número' },
            {
              validator: (_, value) => {
                if (!value) {
                  return Promise.resolve();
                } else if (isNaN(value)) {
                  return Promise.reject(
                    new Error('Debe ser un valor numérico'),
                  );
                }
                if (value < 1) {
                  return Promise.reject(new Error('Debe ser positivo'));
                }
                if (value > 50) {
                  return Promise.reject(
                    new Error('El número es demasiado alto'),
                  );
                }
                if (value.toString().includes('.')) {
                  return Promise.reject(new Error('Debe ser un valor entero'));
                }

                return Promise.resolve();
              },
            },
          ]}
        >
          <InputNumber />
        </Form.Item>
      </Form>
    </Modal>
  );
};

const ProductsList = ({
  handleEdit,
  handleDuplicate,
  deleteDataSource,
  ctmmID,
  reload,
  onMove,
  moveID,
  onSwap,
  swapID,
  lSwap,
  requireElimination,
  unrequireElimination,
}) => {
  const [deleteID, setDeleteID] = useState();

  const handleRequiresDelete = async (record) => {
    if (record.requiere_eliminacion) {
      setDeleteID(record.id);
      await unrequireElimination(record);
      setDeleteID();
    } else requireElimination(record);
  };

  const handleDelete = async (id) => {
    setDeleteID(id);
    await deleteDataSource(id);
    setDeleteID();
  };

  const columns = [
    {
      title: 'Código vigente',
      dataIndex: 'codigo',
      key: 'codigo',
      width: '5rem',
      align: 'center',
    },
    {
      title: 'Productos y/o entregables vigentes',
      dataIndex: 'producto_nombre',
      width: '20rem',
      render: (_, record) => (
        <EllipsisModal
          text={
            record.origen !== 3
              ? record.descripcion_vigente
              : record.producto_nombre
          }
          title="Producto y/o entregable vigente"
        />
      ),
    },
    {
      title: (
        <>
          Tipo{' '}
          <CustomTooltip
            title={
              <>
                <ul>
                  <li>PF: Producto Fijo</li>
                  <li>GF: Gasto Fijo</li>
                  <li>PV: Producto Variable</li>
                  <li>GV: Gasto Variable</li>
                  <li>PVsT: Producto Variable sin Tope</li>
                  <li>GVsT: Gasto Variable sin Tope</li>
                </ul>
              </>
            }
          />
        </>
      ),
      dataIndex: 'tipo',
      width: '5rem',
    },
    {
      title: (
        <>
          Con aprobaciones
          <CustomTooltip
            onClick={() =>
              Modal.info({
                title: 'Estados de aprobación de productos',
                content: (
                  <>
                    <p>Productos Fijos</p>
                    <ul>
                      <li>
                        Si: Estado Aprobado Total, Aprobado Incompleto,
                        Condicionado, No aprobado, No entregado, Pendiente
                      </li>
                      <li>No: Sin aprobar</li>
                    </ul>
                    <br />
                    <p>Productos Variables</p>
                    <ul>
                      <li>
                        Si: Estado Aprobado Total, Aprobado Parcial,
                        Condicionado, No entregado
                      </li>
                      <li>No: Si tiene unidades pendientes o sin aprobar</li>
                    </ul>
                  </>
                ),
              })
            }
            title={<>Estados de aprobación de productos...</>}
          />
        </>
      ),
      dataIndex: 'con_aprobaciones',
      width: '7rem',
      render: (t) => (t ? 'Si' : 'No'),
    },
    {
      title: 'Unidades aprobadas',
      dataIndex: 'unidades_aprobadas',
      width: '6rem',
    },
    {
      title: 'Valor aprobado',
      dataIndex: 'valor_aprobado',
      width: '7rem',
      render: (t) =>
        t
          ? `$ ${t
              .toFixed(2)
              .toString()
              .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`
          : null,
    },
    {
      title: 'Solicitar eliminación',
      dataIndex: 'requiere_eliminacion',
      width: '7rem',
      fixed: 'right',
      render: (_, record) => (
        <>
          {record.origen !== 3 && (
            <Checkbox
              checked={record.requiere_eliminacion}
              disabled={record.con_aprobaciones || deleteID}
              loading={record.id === deleteID}
              onClick={() => handleRequiresDelete(record)}
            />
          )}
        </>
      ),
      align: 'center',
    },
    {
      title: 'Acciones / Justificación',
      key: 'acciones',
      fixed: 'right',
      width: 200,
      render: (_, record) => {
        return (
          <Space direction="horizontal" style={{ width: '100%' }}>
            <Tooltip
              title={
                record.requiere_eliminacion ? 'Justificación' : 'Ver/Editar'
              }
            >
              <IconButton
                icon={<EditFilled style={{ color: IconColors.green }} />}
                onClick={() => handleEdit(record)}
                disabled={
                  record.con_aprobaciones &&
                  (record.maximo_unitario_vigente ? false : true)
                }
              />
            </Tooltip>
            {record.origen === 3 && (
              <>
                <Tooltip
                  title={
                    lSwap && (record.id === swapID || record.id === lSwap)
                      ? 'Cambiando'
                      : record.id === swapID
                        ? 'Deseleccionar'
                        : 'Intercambiar'
                  }
                >
                  <IconButton
                    disabled={
                      !!deleteID ||
                      (lSwap && (record.id === swapID || record.id === lSwap))
                    }
                    onClick={() => onSwap(record)}
                    icon={
                      lSwap && (record.id === swapID || record.id === lSwap) ? (
                        <LoadingOutlined />
                      ) : record.id === swapID ? (
                        <SwapLeftOutlined />
                      ) : swapID ? (
                        <SwapRightOutlined />
                      ) : (
                        <SwapOutlined />
                      )
                    }
                  />
                </Tooltip>
                <IconButton
                  title={
                    moveID && record.id === moveID ? 'Cambiando' : 'Mover a...'
                  }
                  onClick={() => onMove(record)}
                  loading={moveID && record.id === moveID}
                  disabled={!!deleteID}
                  icon={<FullscreenOutlined rotate={45} />}
                />
                <IconButton
                  title="Duplicar"
                  onClick={() => handleDuplicate(record)}
                  icon={<CopyFilled style={{ color: IconColors.green }} />}
                />
                <Tooltip title="Eliminar">
                  <Popconfirm
                    title={MESSAGES.DELETE_CONFIRM_MSG}
                    onConfirm={async () => {
                      await handleDelete(record.id);
                    }}
                  >
                    <IconButton
                      disabled={!!deleteID}
                      icon={
                        <DeleteFilled
                          style={{ color: IconColors.red }}
                          rotate={record.id === deleteID ? 45 : 0}
                        />
                      }
                    />
                  </Popconfirm>
                </Tooltip>
              </>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Estado',
      dataIndex: 'origen',
      width: '8rem',
      fixed: 'right',
      render: (_, record) =>
        record.origen === 1 ? '' : record.origen === 2 ? 'Modificado' : 'Nuevo',
      align: 'center',
    },
  ];

  return (
    <AjaxTable
      bordered
      sticky={{
        offsetHeader: -40,
      }}
      columns={columns}
      searchInput
      tableTitle={'Listado de productos'}
      endpoint={filterAjaxTable}
      reloadButton
      reloadSource={reload}
      scroll={{ x: false }}
      query={ctmmID}
      showSizeChanger
      pageSize={50}
      exportCSV
    />
  );
};

const JustificationForm = memo(({ initialValues, id, onFinish }) => {
  const [loading, setLoading] = useState(false);

  const save = async (data) => {
    try {
      setLoading(true);
      const res = await patch(id, { ...data, requiere_eliminacion: true });
      notification.success({
        message: MESSAGES.SUCCESS_MSG,
        description: MESSAGES.DESC_SUCCESS_MSG,
        duration: 2,
      });
      setLoading(false);
      if (!initialValues.tipo.endsWith('sT') && res.data.pagos.length > 0) {
        Modal.info({
          title: 'Pagos asociados',
          content: `Atención, la modificación de este ${
            initialValues.tipo.startsWith('P') ? 'producto' : 'gasto/ítem'
          } está asignado a los siguientes pagos: ${
            res.data.pagos.length === 1
              ? res.data.pagos[0]
              : `${res.data.pagos.slice(0, -1).join(', ')} y ${
                  res.data.pagos[res.data.pagos.length - 1]
                }`
          }. Por favor verifique con atención y diríjase al módulo de Forma de pago para ajustarlo(s) en caso de ser requerido`,
          onOk: onFinish,
        });
      } else {
        onFinish();
      }
    } catch (err) {
      console.log(err);
      notification.error({
        message: MESSAGES.ERROR_MSG,
        description: MESSAGES.DESC_ERROR_POST_MSG,
        duration: 2,
      });
      setLoading(false);
    }
  };

  const buttonItemLayout = {
    wrapperCol: {
      span: 10,
      offset: 11,
    },
  };

  return (
    <Form
      onFinish={save}
      autoComplete="off"
      layout="vertical"
      initialValues={initialValues}
    >
      <br />
      <Paragraph>Producto y/o entregable No. {initialValues.codigo}</Paragraph>
      <div className="ant-form-item">
        <Row gutter={300}>
          <Col>
            <Text className="ant-form-item-label">
              Producto y/o entregable de el/la contratista
              <b style={{ color: 'rgb(51, 51, 255)' }}> vigente</b>
            </Text>
          </Col>
        </Row>
        <TextArea
          rows={4}
          bordered
          allowClear
          value={initialValues.descripcion_vigente}
          disabled
        />
      </div>
      <Form.Item
        name="justificacion"
        label="Justificación de la solicitud de eliminación"
        rules={[
          {
            required: true,
            message: 'Este campo es obligatorio!',
          },
        ]}
      >
        <TextArea rows={4} bordered allowClear />
      </Form.Item>
      <Form.Item {...buttonItemLayout}>
        <CustomButton loading={loading} text="Editar" />
      </Form.Item>
    </Form>
  );
});

const ProductEditForm = ({ editData, onFinish }) => {
  const [loading, setLoading] = useState(false);
  const [initialValues, setInitialValues] = useState();

  useEffect(() => {
    async function fetchData(id) {
      setInitialValues();
      try {
        const res = await get(id);
        setInitialValues({
          ...res.data,
          labelType: res.data.es_apoyo ? 'gasto/ítem' : 'producto',
        });
      } catch (error) {
        catchException(error);
      }
    }

    fetchData(editData.id);
  }, [editData.id]);

  const save = async (data) => {
    try {
      setLoading(true);
      const sendData = { ...data };
      delete sendData['variable'];
      delete sendData['es_periodica'];
      // delete sendData['valor_total_vigente'];
      delete sendData['tiene_tope'];

      // delete sendData['nombre_unitario_vigente'];
      // delete sendData['valor_unitario_vigente'];
      // delete sendData['maximo_unitario_vigente'];

      const res = await patch(initialValues.id, data);
      notification.success({
        message: MESSAGES.SUCCESS_MSG,
        description: MESSAGES.DESC_SUCCESS_MSG,
        duration: 2,
      });
      setLoading(false);
      if (!editData.tipo.endsWith('sT') && res.data.pagos.length > 0) {
        Modal.info({
          title: 'Pagos asociados',
          content: `Atención, la modificación de este ${
            initialValues.labelType
          } está asignado a los siguientes pagos: ${
            res.data.pagos.length === 1
              ? res.data.pagos[0]
              : `${res.data.pagos.slice(0, -1).join(', ')} y ${
                  res.data.pagos[res.data.pagos.length - 1]
                }`
          }. Por favor verifique con atención y diríjase al módulo de Forma de pago para ajustarlo(s) en caso de ser requerido`,
          onOk: onFinish,
        });
      } else {
        onFinish();
      }
    } catch (err) {
      catchException(err);
      setLoading(false);
    }
  };

  if (!initialValues) return <Skeleton loading />;

  return (
    <Form
      onFinish={save}
      autoComplete="off"
      // layout="vertical"
      initialValues={initialValues}
      colon={false}
    >
      <br />
      <Paragraph>Producto y/o entregable No. {initialValues.codigo}</Paragraph>
      <div className="ant-form-item">
        <Radio.Group
          style={{ marginLeft: '2rem' }}
          value={initialValues.es_apoyo}
          disabled
        >
          <Radio value={false}>Producto y/o entregable</Radio>
          <Radio value={true}>Gastos u otros ítems</Radio>
        </Radio.Group>
      </div>
      {initialValues.es_apoyo ? (
        <>
          <div
            className="ant-row ant-form-item"
            style={{ alignItems: 'center' }}
          >
            <Text
              className="ant-form-item-label ant-col"
              style={{ marginRight: '10px' }}
            >
              Gasto
              <b style={{ color: 'rgb(51, 51, 255)' }}> vigente</b>
            </Text>
            <div className="ant-form-item-control ant-col">
              <Select
                disabled
                value={0}
                options={[
                  { value: 0, label: initialValues.tipo_apoyo_vigente },
                ]}
              />
            </div>
          </div>
          {!editData.con_aprobaciones && (
            <Form.Item
              label={
                <div>
                  Gasto
                  <b style={{ color: 'rgb(0, 153, 0)' }}> modificado</b>
                </div>
              }
              name="tipo_apoyo"
              rules={[
                {
                  required: true,
                  message: 'Este campo es obligatorio!',
                },
              ]}
              valuePropName="initialValue"
            >
              <CustomSelector endpointFetch={getSupportTypes} />
            </Form.Item>
          )}
          {initialValues.tipo_apoyo_vigente === 'Otro' ? (
            <div className="ant-form-item">
              <Text className="ant-form-item-label">
                Concepto del {initialValues.labelType}
                <b style={{ color: 'rgb(51, 51, 255)' }}> vigente</b>
              </Text>
              <TextArea
                rows={4}
                bordered
                allowClear
                value={initialValues.descripcion_vigente}
                disabled
              />
            </div>
          ) : null}
          {!editData.con_aprobaciones && (
            <Form.Item noStyle dependencies={['tipo_apoyo']}>
              {({ getFieldValue }) =>
                getFieldValue('tipo_apoyo') === 100 ? (
                  <Form.Item
                    label={
                      <div>
                        Digite el concepto del {initialValues.labelType}
                        <b style={{ color: 'rgb(0, 153, 0)' }}> modificado</b>
                      </div>
                    }
                    name="descripcion"
                    rules={[
                      {
                        required: true,
                        message: 'Este campo es obligatorio!',
                      },
                    ]}
                    tooltip={{
                      placement: 'top',
                      title: `No use las palabras "Apoyo" o "Auxilio" en el concepto del ${initialValues.labelType}.`,
                    }}
                    labelCol={{ span: 24 }}
                    wrapperCol={{ span: 24 }}
                  >
                    <TextArea rows={4} bordered allowClear />
                  </Form.Item>
                ) : null
              }
            </Form.Item>
          )}
        </>
      ) : (
        <>
          <div className="ant-form-item">
            <Text className="ant-form-item-label">
              Producto y/o entregable de el/la contratista
              <b style={{ color: 'rgb(51, 51, 255)' }}> vigente</b>
            </Text>
            <TextArea
              rows={4}
              bordered
              allowClear
              value={initialValues.descripcion_vigente}
              disabled
            />
          </div>
          {!editData.con_aprobaciones && (
            <Form.Item
              name="descripcion"
              label={
                <div>
                  Producto y/o entregable de el/la contratista
                  <b style={{ color: 'rgb(0, 153, 0)' }}> modificado</b>
                </div>
              }
              rules={[
                {
                  required: true,
                  message: 'Este campo es obligatorio!',
                },
              ]}
              labelCol={{ span: 24 }}
              wrapperCol={{ span: 24 }}
            >
              <TextArea rows={4} bordered allowClear />
            </Form.Item>
          )}
        </>
      )}
      <div className="ant-form-item">
        <Text className="ant-form-item-label">
          ¿El contratista debe cargar soportes/evidencias en SIGA para la
          aprobación de este producto/gasto?
          <b style={{ color: 'rgb(51, 51, 255)' }}> vigente</b>
        </Text>
        <Radio.Group
          style={{ marginLeft: '2rem' }}
          value={initialValues.requiere_evidencias_vigente}
          disabled
        >
          <Radio value={true}>Si</Radio>
          <Radio value={false}>No</Radio>
        </Radio.Group>
      </div>
      {!editData.con_aprobaciones && (
        <Form.Item
          name="requiere_evidencias"
          label={
            <div>
              ¿El contratista debe cargar soportes/evidencias en SIGA para la
              aprobación de este producto/gasto?
              <b style={{ color: 'rgb(0, 153, 0)' }}> modificado</b>
            </div>
          }
          rules={[
            {
              required: true,
              message: 'Tiene que escoger una opción',
            },
          ]}
        >
          <Radio.Group style={{ marginLeft: '2rem' }}>
            <Radio value={true}>Si</Radio>
            <Radio value={false}>No</Radio>
          </Radio.Group>
        </Form.Item>
      )}

      <Form.Item
        name="variable"
        label={
          <div>
            ¿Este {initialValues.labelType} tiene pago fijo o variable?
            <b style={{ color: 'rgb(51, 51, 255)' }}> vigente</b>
          </div>
        }
        tooltip={{
          placement: 'top',
          title: `Valor unitario según ${initialValues.labelType} (horas, talleres, tutorías, etc)`,
        }}
      >
        <Radio.Group style={{ marginLeft: '2rem' }} disabled>
          <Radio value={false}>Fijo</Radio>
          <Radio value={true}>Variable</Radio>
        </Radio.Group>
      </Form.Item>

      {initialValues.variable ? (
        <>
          <Form.Item
            name="tiene_tope"
            label={
              <div>
                ¿Este {initialValues.labelType} tiene topes?
                <b style={{ color: 'rgb(51, 51, 255)' }}> vigente</b>
              </div>
            }
            tooltip={{
              placement: 'top',
              title: `Si el ${initialValues.labelType} tiene un máximo a pagar y un máximo de unidades`,
            }}
          >
            <Radio.Group style={{ marginLeft: '2rem' }} disabled>
              <Radio value={true}>Si</Radio>
              <Radio value={false}>No</Radio>
            </Radio.Group>
          </Form.Item>
          {initialValues.tiene_tope ? (
            <>
              <Form.Item
                name="maximo_unitario_vigente"
                label={
                  <div>
                    Tope de unidades máximas a pagar de este{' '}
                    {initialValues.labelType}
                    <b style={{ color: 'rgb(51, 51, 255)' }}> vigente</b>
                  </div>
                }
              >
                <InputNumber
                  style={{ width: '100%' }}
                  disabled
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                  }
                  parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
                />
              </Form.Item>
              <Form.Item
                name="maximo_unitario"
                label={
                  <div>
                    Tope de unidades máximas a pagar de este{' '}
                    {initialValues.labelType}
                    <b style={{ color: 'rgb(0, 153, 0)' }}> modificado</b>
                  </div>
                }
                rules={[
                  {
                    required: true,
                    message: 'Este campo es obligatorio!',
                  },
                  {
                    validator: (_, value) => {
                      if (!value) {
                        return Promise.resolve();
                      } else if (isNaN(value)) {
                        return Promise.reject(
                          new Error('Debe ser un valor numérico'),
                        );
                      }
                      if (value < 0) {
                        return Promise.reject(new Error('Debe ser positivo'));
                      }
                      if (value < 1.01) {
                        return Promise.reject(
                          new Error(
                            'Debe ser mayor a 1 o en caso contrario seleccione pago fijo',
                          ),
                        );
                      }
                      if (
                        value.toString().includes('.') &&
                        value.toString().split('.', 2)[1].length > 2
                      ) {
                        return Promise.reject(
                          new Error(
                            'Debe ser un valor con máximo dos decimales',
                          ),
                        );
                      }
                      if (
                        editData.con_aprobaciones &&
                        value < initialValues.unidades_aprobadas
                      ) {
                        // Units cant be less than the assigned to payments already sent to disbursement
                        return Promise.reject(
                          new Error(
                            'Valor inferior a las unidades asignadas a pagos aprobados, por favor verifique',
                          ),
                        );
                      }

                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                  }
                  parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
                />
              </Form.Item>
            </>
          ) : null}
          <Form.Item
            name="nombre_unitario_vigente"
            label={
              <div>
                Nombre de la unidad de este {initialValues.labelType}
                <b style={{ color: 'rgb(51, 51, 255)' }}> vigente</b>
              </div>
            }
            tooltip={{
              placement: 'top',
              title: 'Digite en singular si es hora, taller, tutoría, etc.',
            }}
          >
            <Input disabled />
          </Form.Item>
          {editData.con_aprobaciones ? null : (
            <Form.Item
              name="nombre_unitario"
              label={
                <div>
                  Nombre de la unidad de este {initialValues.labelType}
                  <b style={{ color: 'rgb(0, 153, 0)' }}> modificado</b>
                </div>
              }
              tooltip={{
                placement: 'top',
                title: 'Digite en singular si es hora, taller, tutoría, etc.',
              }}
              rules={[
                {
                  required: true,
                  message: 'Este campo es obligatorio!',
                },
                {
                  type: 'string',
                  max: 50,
                  message:
                    'El nombre de la unidad no debe de superar los 50 caracteres',
                },
                {
                  type: 'string',
                  pattern: /^[a-záéíóúñü (){}[\]_%+"-]+$/,
                  message: 'Utilice unicamente minúsculas',
                },
              ]}
            >
              <Input />
            </Form.Item>
          )}
          <Form.Item
            name="valor_unitario_vigente"
            label={
              <div>
                Valor de la unidad de este {initialValues.labelType}
                <b style={{ color: 'rgb(51, 51, 255)' }}> vigente</b>
              </div>
            }
            tooltip={{
              placement: 'top',
              title: `Valor unitario según ${initialValues.labelType} (horas, talleres, tutorías, etc)`,
            }}
          >
            <InputNumber
              style={{ width: '100%' }}
              disabled
              formatter={(value) =>
                `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
              }
              parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
            />
          </Form.Item>
          {editData.con_aprobaciones ? null : (
            <Form.Item
              name="valor_unitario"
              label={
                <div>
                  Valor de la unidad de este {initialValues.labelType}
                  <b style={{ color: 'rgb(0, 153, 0)' }}> modificado</b>
                </div>
              }
              tooltip={{
                placement: 'top',
                title: `Valor unitario según ${initialValues.labelType} (horas, talleres, tutorías, etc)`,
              }}
              rules={[
                {
                  required: true,
                  message: 'Este campo es obligatorio!',
                },
                {
                  validator: (_, value) => {
                    if (!value) {
                      return Promise.resolve();
                    } else if (isNaN(value)) {
                      return Promise.reject(
                        new Error('Debe ser un valor numérico'),
                      );
                    }
                    if (value < 0) {
                      return Promise.reject(new Error('Debe ser positivo'));
                    }
                    if (
                      editData?.currencyType &&
                      editData.currencyType !== 'COP'
                    ) {
                      if (
                        value.toString().includes('.') &&
                        value.toString().split('.', 2)[1].length > 2
                      ) {
                        return Promise.reject(
                          new Error(
                            'Debe ser un valor con máximo dos decimales',
                          ),
                        );
                      }
                    } else if (value.toString().includes('.')) {
                      return Promise.reject(
                        new Error('Debe ser un valor entero'),
                      );
                    }

                    return Promise.resolve();
                  },
                },
              ]}
            >
              <InputNumber
                style={{ width: '100%' }}
                formatter={(value) =>
                  `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                }
                parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
              />
            </Form.Item>
          )}

          {initialValues.tiene_tope ? (
            <>
              <Form.Item
                name="valor_total_vigente"
                label={
                  <div>
                    Valor máximo a pagar por este {initialValues.labelType}
                    <b style={{ color: 'rgb(51, 51, 255)' }}> vigente</b>
                  </div>
                }
              >
                <InputNumber
                  style={{ width: '100%' }}
                  formatter={(value) =>
                    `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                  }
                  disabled
                />
              </Form.Item>
              <Form.Item
                noStyle
                dependencies={['maximo_unitario', 'valor_unitario']}
              >
                {({ getFieldValue }) => (
                  <Row align="middle" style={{ marginBottom: '10px' }}>
                    <Col flex="22rem">
                      {`Valor máximo a pagar por este ${initialValues.labelType}`}{' '}
                      <b style={{ color: 'rgb(0, 153, 0)' }}>
                        {' '}
                        modificado
                      </b>{' '}
                    </Col>
                    <Col flex="auto">
                      <InputNumber
                        style={{ width: '100%' }}
                        formatter={(value) =>
                          `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                        }
                        value={(
                          (getFieldValue('valor_unitario') || 0) *
                          (getFieldValue('maximo_unitario') || 0)
                        ).toFixed(2)}
                        disabled
                      />
                    </Col>
                  </Row>
                )}
              </Form.Item>
            </>
          ) : null}
        </>
      ) : (
        <>
          {!initialValues.es_apoyo && (
            <Form.Item
              name="es_periodica"
              label={
                <div>
                  ¿La entrega de este {initialValues.labelType} es periódica?
                  <b style={{ color: 'rgb(51, 51, 255)' }}> vigente</b>
                </div>
              }
              tooltip={{
                placement: 'top',
                title: 'Ejemplo: Entrega mensual por parte del contratista',
              }}
            >
              <Radio.Group style={{ marginLeft: '2rem' }} disabled>
                <Radio value={true}>Si</Radio>
                <Radio value={false}>No</Radio>
              </Radio.Group>
            </Form.Item>
          )}
          <Form.Item
            name="valor_total_vigente"
            label={
              <div>
                Valor del {initialValues.labelType} para el pago fijo
                <b style={{ color: 'rgb(51, 51, 255)' }}> vigente</b>
              </div>
            }
          >
            <InputNumber
              disabled
              style={{ width: '100%' }}
              formatter={(value) =>
                `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
              }
              parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
            />
          </Form.Item>

          <Form.Item
            name="valor_total"
            label={
              <div>
                Digite el valor del {initialValues.labelType} para el pago fijo
                <b style={{ color: 'rgb(0, 153, 0)' }}> modificado</b>
              </div>
            }
            rules={[
              {
                required: true,
                message: 'Este campo es obligatorio!',
              },
              {
                validator: (_, value) => {
                  if (!value) {
                    return Promise.resolve();
                  } else if (isNaN(value)) {
                    return Promise.reject(
                      new Error('Debe ser un valor numérico'),
                    );
                  }
                  if (value < 0) {
                    return Promise.reject(new Error('Debe ser positivo'));
                  }

                  if (
                    editData?.currencyType &&
                    editData.currencyType !== 'COP'
                  ) {
                    if (
                      value.toString().includes('.') &&
                      value.toString().split('.', 2)[1].length > 2
                    ) {
                      return Promise.reject(
                        new Error('Debe ser un valor con máximo dos decimales'),
                      );
                    }
                  } else if (value.toString().includes('.')) {
                    return Promise.reject(
                      new Error('Debe ser un valor entero'),
                    );
                  }

                  return Promise.resolve();
                },
              },
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              formatter={(value) =>
                `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
              }
              parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
            />
          </Form.Item>
        </>
      )}
      <Form.Item
        name="justificacion"
        label="Justificación"
        rules={[
          {
            required: true,
            message: 'Este campo es obligatorio!',
          },
        ]}
        labelCol={{ span: 24 }}
      >
        <TextArea rows={4} bordered allowClear />
      </Form.Item>
      <Form.Item
        wrapperCol={{
          span: 10,
          offset: 11,
        }}
      >
        <CustomButton loading={loading} text="Editar" />
      </Form.Item>
    </Form>
  );
};

const ProductForm = memo(({ initialValues, onFinish, info }) => {
  const [loading, setLoading] = useState(false);

  const save = async (data) => {
    try {
      if (!initialValues) {
        const newData = {
          modulo_modificacion: info.ctmmID,
          ...data,
        };
        setLoading(true);
        await post(newData);
      } else {
        setLoading(true);
        await patch(initialValues.id, data);
      }
      notification.success({
        message: MESSAGES.SUCCESS_MSG,
        description: MESSAGES.DESC_SUCCESS_MSG,
        duration: 2,
      });
      setLoading(false);
      onFinish();
    } catch (err) {
      console.log(err);
      notification.error({
        message: MESSAGES.ERROR_MSG,
        description: MESSAGES.DESC_ERROR_POST_MSG,
        duration: 2,
      });
      setLoading(false);
    }
  };

  return (
    <Form onFinish={save} autoComplete="off" initialValues={initialValues}>
      {initialValues && (
        <>
          <br />
          <Paragraph>
            Producto y/o entregable No. {initialValues.codigo}
          </Paragraph>
        </>
      )}
      <ProductFields
        onlyUnlimited={info.onlyUnlimited}
        currencyType={info.currencyType}
        alwaysDetailed
      />
      <Form.Item
        name="justificacion"
        label="Justificación"
        rules={[
          {
            required: true,
            message: 'Este campo es obligatorio!',
          },
        ]}
        labelCol={{ span: 24 }}
      >
        <TextArea rows={4} bordered allowClear />
      </Form.Item>
      <Form.Item
        wrapperCol={{
          span: 10,
          offset: 11,
        }}
      >
        <CustomButton
          loading={loading}
          text={initialValues?.id ? 'Editar' : 'Agregar'}
        />
      </Form.Item>
    </Form>
  );
});

export default function ModificationProductsForm({ sendPerm }) {
  const { modification_id } = useParams();
  const [dataInfo, setDataInfo] = useState();
  const [swapData, setSwapData] = useState(undefined);
  const [lSwap, setLSwap] = useState(undefined);
  const [moveData, setMoveData] = useState(undefined);
  const [duplicateData, setDuplicateData] = useState(undefined);
  const [loadingForm, setLoadingForm] = useState(false);

  const [reloadTable, setReloadTable] = useState();

  const [form] = Form.useForm();
  const navigate = useHistory();

  const [editData, setEditData] = useState(undefined);
  const [justificationData, setJustificationData] = useState(undefined);
  const [createNew, setCreateNew] = useState(false);

  const [openPreview, setOpenPreview] = useState(false);

  const [lRenumerate, setLRenumerate] = useState(false);

  const breadcrumbItems = [
    {
      text: 'Módulo Modificaciones Contractuales',
      to: `${INTERNAL_LINKS.CONTRACT_MODIFICATIONS}`,
    },
    {
      text: 'Formulario de Modificaciones Contractuales',
      to: `${INTERNAL_LINKS.CONTRACT_MODIFICATIONS}-form/${
        dataInfo?.proyecto_id ?? 0
      }/${modification_id}`,
    },
    {
      text: 'Productos y/o entregables de el/la contratista',
    },
  ];

  useEffect(() => {
    let mounted = true;
    async function fetchData() {
      try {
        const res = await info(modification_id);
        if (mounted) setDataInfo(res.data);
      } catch (error) {
        notification.error({
          message: MESSAGES.ERROR_MSG,
          description: MESSAGES.DESC_ERROR_GET_MSG,
          duration: 2,
        });
      }
    }
    fetchData();
    return () => {
      mounted = false;
    };
  }, [modification_id]);

  const handleEdit = (record) => {
    if (record.requiere_eliminacion) {
      setJustificationData(record);
    } else {
      setEditData({
        ...record,
        currencyType: dataInfo.moneda_sigla,
        onlyUnlimited: dataInfo.unicamente_sin_tope,
      });
    }
  };

  const handleDuplicate = (item) => {
    setDuplicateData(item);
  };

  const handleDuplicateClose = (r) => {
    setDuplicateData();
    if (r) doReload();
  };

  const doReload = () => {
    setReloadTable({});
  };

  const doSwap = async (a, b) => {
    try {
      setLSwap(b);
      await swap(a, b);
      notification.success({
        message: MESSAGES.SUCCESS_MSG,
        description: MESSAGES.DESC_SUCCESS_MSG,
        duration: 2,
      });
      setSwapData(undefined);
      setLSwap(undefined);
      doReload();
    } catch (error) {
      catchException(error);
    }
  };

  const onSwap = (row) => {
    if (swapData) {
      if (row.id === swapData.id) setSwapData(undefined);
      else doSwap(swapData.id, row.id);
    } else {
      setSwapData(row);
    }
  };

  const onMove = (row) => {
    setMoveData(row);
  };

  const doMove = async (values) => {
    try {
      setMoveData((s) => ({ ...s, loading: true }));
      await move(moveData.id, values);
      setMoveData();
      setReloadTable({});
    } catch (error) {
      setMoveData((s) => ({ ...s, loading: false }));
      catchException(error);
    }
  };

  const unrequireElimination = async (id) => {
    try {
      await patch(id, { requiere_eliminacion: false, justificacion: '' });
      notification.success({
        message: MESSAGES.SUCCESS_MSG,
        description: MESSAGES.DESC_SUCCESS_MSG,
      });
      setReloadTable({});
    } catch (err) {
      console.log(err);
      notification.error({
        message: MESSAGES.ERROR_MSG,
        description: MESSAGES.DESC_ERROR_POST_MSG,
        duration: 2,
      });
    }
  };

  // Delete Validation
  const deleteDataSource = async (id) => {
    try {
      await remove(id);
      notification.success({
        message: MESSAGES.SUCCESS_MSG,
        description: MESSAGES.DESC_SUCCESS_DELETE_MSG,
      });
      if (id === swapData?.id) setSwapData(undefined);
      setReloadTable({});
    } catch (err) {
      console.log(err);
      notification.error({
        message: MESSAGES.ERROR_MSG,
        description:
          'Este producto ya se encuentra vinculada a un pago. Por favor, primero, elimine la forma de pago y, luego proceda a eliminar el producto',
        duration: 2,
      });
    }
  };

  const onSave = () => {
    if (sendPerm) form.submit();
    else
      navigate.push(
        `${INTERNAL_LINKS.CONTRACT_MODIFICATIONS}-form/${dataInfo.proyecto_id}/${modification_id}`,
      );
  };

  const onFinish = async (values) => {
    try {
      setLoadingForm(true);
      await patchModule(dataInfo.id, values);
      notification.success({
        message: MESSAGES.SUCCESS_MSG,
        description: MESSAGES.DESC_SUCCESS_MSG,
        duration: 2,
      });
      setTimeout(() => {
        navigate.push(
          `${INTERNAL_LINKS.CONTRACT_MODIFICATIONS}-form/${dataInfo.proyecto_id}/${modification_id}`,
        );
      }, MESSAGES.TIME);
    } catch (error) {
      notification.error({
        message: MESSAGES.ERROR_MSG,
        description: MESSAGES.DESC_ERROR_POST_MSG,
        duration: 2,
      });
    } finally {
      setLoadingForm(false);
    }
  };

  const doRenumerate = async () => {
    try {
      setLRenumerate(true);
      await renumerate(modification_id);
      doReload();
      setLRenumerate(false);
      notification.success({
        message: MESSAGES.SUCCESS_MSG,
        description: MESSAGES.DESC_SUCCESS_MSG,
        duration: 2,
      });
    } catch (error) {
      catchException(error);
      setLRenumerate(false);
    }
  };

  return (
    <>
      <BreadcrumbContainer items={breadcrumbItems} />
      <div className="box-title">
        <Title className={'text-uppercase'} level={2}>
          MODIFICACIÓN CONTRACTUAL PRODUCTOS
        </Title>
      </div>
      {dataInfo ? (
        <>
          <Space direction={'vertical'} style={{ width: '100%' }}>
            <Descriptions
              layout="horizontal"
              column={2}
              bordered
              labelStyle={{ maxWidth: '5rem' }}
              className="fade-in"
            >
              <Descriptions.Item label="Contratista / Proveedor" span={2}>
                <Row align="middle">
                  <Col flex="auto">{dataInfo.contratista_nombre}</Col>
                  <Col flex="0 0">
                    <CustomButton
                      htmlType="button"
                      text={
                        <Link
                          to={`${INTERNAL_LINKS.RESUME_CONTRACT_SEARCH}-detail/${dataInfo.contrato_id}/${dataInfo.proyecto_id}/${dataInfo.cargo_id}/`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Ver históricos contractuales
                        </Link>
                      }
                    />
                  </Col>
                </Row>
              </Descriptions.Item>
              <Descriptions.Item label="Cargo / Rol" span={2}>
                {dataInfo?.cargo_nombre}
              </Descriptions.Item>
            </Descriptions>
          </Space>
          <br />
          <br />
          <Card
            size={'small'}
            bordered={false}
            title={
              <Title level={3}>3. Ajuste de productos y/o entregables:</Title>
            }
          >
            {sendPerm && (
              <Form
                form={form}
                layout="vertical"
                autoComplete="off"
                onFinish={onFinish}
                initialValues={{
                  clausula: dataInfo.clausula,
                }}
              >
                <Form.Item
                  label="Digite en letras el número de la cláusula del contrato a modificar"
                  name="clausula"
                  rules={[
                    {
                      required: true,
                      message: 'Ingrese el nombre de la cláusula',
                    },
                    {
                      max: 100,
                      message:
                        'El campo debe tener un máximo de 100 caracteres',
                    },
                  ]}
                >
                  <Input
                    type="text"
                    allowClear
                    placeholder=""
                    name="name_clause"
                  />
                </Form.Item>
              </Form>
            )}
          </Card>

          <Paragraph>
            Por favor, de los siguientes productos y/o entregables modifique los
            que requiera
          </Paragraph>
          <Space direction="vertical">
            <PaPreviewModal contratoID={dataInfo.contrato_id} />
            <CustomButton
              htmlType="button"
              text="Renumerar"
              onClick={doRenumerate}
              loading={lRenumerate}
            />
          </Space>
          {swapData && (
            <>
              <Alert
                message="Intercambio de código"
                description={
                  <>
                    Se ha seleccionado para intercambio de código el item
                    <br />
                    <br />
                    Código {swapData.codigo}: {swapData.descripcion}
                  </>
                }
                type={'info'}
                showIcon
              />
            </>
          )}
          <DuplicateModal
            record={duplicateData}
            onClose={handleDuplicateClose}
          />
          <ProductsList
            ctmmID={dataInfo.id}
            handleEdit={handleEdit}
            handleDuplicate={handleDuplicate}
            deleteDataSource={deleteDataSource}
            reload={reloadTable}
            onMove={onMove}
            moveID={moveData?.id}
            onSwap={onSwap}
            swapID={swapData?.id}
            lSwap={lSwap}
            requireElimination={(record) => setJustificationData(record)}
            unrequireElimination={async (record) =>
              await unrequireElimination(record.id)
            }
          />

          <Paragraph>
            Para agregar un nuevo producto y/o entregable de clic en el botón
            "Agregar"{' '}
            <CustomButton
              htmlType="button"
              text="Agregar"
              onClick={() => setCreateNew(true)}
            />
          </Paragraph>

          <Modal
            title={
              <Title level={4} style={{ marginBottom: 0 }}>
                Modificación Contractual
              </Title>
            }
            destroyOnClose={true}
            footer={null}
            open={editData && editData.origen !== 3}
            onCancel={() => setEditData(undefined)}
            width={'80%'}
          >
            <b>Modificación de producto y/o entregable</b>
            <ProductEditForm
              editData={editData}
              onFinish={() => {
                doReload();
                setEditData();
                setCreateNew(false);
              }}
            />
          </Modal>

          <Modal
            title={
              <Title level={4} style={{ marginBottom: 0 }}>
                Modificación Contractual
              </Title>
            }
            destroyOnClose={true}
            footer={null}
            open={!!justificationData}
            onCancel={() => setJustificationData(undefined)}
            width={'80%'}
          >
            <b>Solicitud de eliminación</b>
            <JustificationForm
              initialValues={justificationData}
              onFinish={() => {
                doReload();
                setJustificationData(undefined);
              }}
              id={justificationData?.id}
            />
          </Modal>

          <Modal
            title={
              <Title level={4} style={{ marginBottom: 0 }}>
                Modificación Contractual
              </Title>
            }
            destroyOnClose={true}
            footer={null}
            open={createNew || editData?.origen === 3}
            onCancel={() => {
              setEditData();
              setCreateNew(false);
            }}
            width={'80%'}
          >
            {editData?.origen === 3 ? (
              <b>Modificación de producto y/o entregable</b>
            ) : (
              <b>Agregar un nuevo producto y/o entregable</b>
            )}
            <ProductForm
              initialValues={editData}
              info={{
                ctmmID: dataInfo.id,
                currencyType: dataInfo.moneda_sigla,
                onlyUnlimited: dataInfo.unicamente_sin_tope,
              }}
              onFinish={() => {
                doReload();
                setEditData();
                setCreateNew(false);
              }}
            />
          </Modal>

          <Modal
            title={
              <Title level={4} style={{ marginBottom: 0 }}>
                Modificación Contractual
              </Title>
            }
            destroyOnClose={true}
            footer={null}
            open={openPreview}
            onCancel={() => setOpenPreview(false)}
            width={'80%'}
          >
            <b>Previsualización de productos y/o entregables</b>
            <AjaxTable
              columns={[
                {
                  title: 'Código vigente',
                  dataIndex: 'codigo',
                  key: 'codigo',
                  width: '5rem',
                  align: 'center',
                },
                {
                  title: 'Productos y/o entregables vigentes',
                  dataIndex: 'producto_nombre',
                  key: 'descripcion',
                  width: '20rem',
                  render: (t) => (
                    <EllipsisModal
                      text={t}
                      title="Producto y/o entregable vigente"
                    />
                  ),
                },
                {
                  title: (
                    <>
                      Tipo{' '}
                      <CustomTooltip
                        title={
                          <>
                            <ul>
                              <li>PF: Producto Fijo</li>
                              <li>GF: Gasto Fijo</li>
                              <li>PV: Producto Variable</li>
                              <li>GV: Gasto Variable</li>
                              <li>PVsT: Producto Variable sin Tope</li>
                              <li>GVsT: Gasto Variable sin Tope</li>
                            </ul>
                          </>
                        }
                      />
                    </>
                  ),
                  dataIndex: 'tipo',
                  width: '5rem',
                },
                {
                  title: 'Unidades máximas',
                  dataIndex: 'maximo_unitario',
                  width: '6rem',
                },
                {
                  title: 'Unidad',
                  dataIndex: 'nombre_unitario',
                  width: '6rem',
                },
                {
                  title: 'Valor unidad',
                  dataIndex: 'valor_unitario',
                  width: '7rem',
                  render: (t) =>
                    t
                      ? `$ ${t
                          .toFixed(2)
                          .toString()
                          .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`
                      : null,
                },
                {
                  title: 'Valor máximo a pagar',
                  dataIndex: 'valor_total',
                  width: '7rem',
                  render: (t) =>
                    t
                      ? `$ ${t
                          .toFixed(2)
                          .toString()
                          .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`
                      : null,
                },
              ]}
              searchInput
              tableTitle={'Listado de productos'}
              endpoint={preview}
              scroll={{ x: false }}
              query={dataInfo.id}
              exportCSV
            />
          </Modal>

          <Modal
            open={moveData?.id}
            title={
              <Title level={4} style={{ marginBottom: 0 }}>
                Mover a...
              </Title>
            }
            footer={null}
            closable
            onCancel={() => setMoveData()}
            destroyOnClose
          >
            <Form onFinish={doMove}>
              <Form.Item
                name="new_code"
                label="Nuevo código"
                rules={[
                  { required: true, message: 'El nuevo código es requerido' },
                  {
                    type: 'number',
                    min: 1,
                    message: 'Ingrese un valor positivo',
                  },
                ]}
              >
                <InputNumber style={{ width: '100%' }} />
              </Form.Item>
              <Space align="end">
                <CustomButton
                  htmlType="button"
                  className={'back-button'}
                  type="default"
                  text="Volver"
                  onClick={() => setMoveData()}
                />
                <CustomButton
                  loading={moveData?.loading}
                  htmlType="submit"
                  text="Mover"
                />
              </Space>
            </Form>
          </Modal>

          <Space size="large">
            <CustomButton
              htmlType="button"
              className={'back-button'}
              type="default"
              text={
                <Link
                  to={`${INTERNAL_LINKS.CONTRACT_MODIFICATIONS}-form/${dataInfo.proyecto_id}/${modification_id}`}
                >
                  Volver
                </Link>
              }
            />

            <CustomButton
              htmlType="button"
              type="default"
              text="Previsualizar cambios"
              onClick={() => setOpenPreview(true)}
            />

            <ModificationModuleSaveButton
              onConfirm={onSave}
              loading={loadingForm}
            />
          </Space>
        </>
      ) : (
        <Skeleton active></Skeleton>
      )}
    </>
  );
}
